/**
 * Batch 18-F32 — pure derivation + copy constants for the
 * technician-facing Training Evidence Summary.
 *
 * Lives in utils/ rather than inside the component so the
 * derivation can be exhaustively unit-tested without DOM noise
 * and so the component file can stay strictly presentational.
 *
 * Privacy / boundary contract (locked by the F31 plan):
 *   - Pure function. No I/O. No fetch. No Supabase. No Edge
 *     Function call. No completion-state mutation.
 *   - Operates only on the typed `TrainingEvidenceDisplayRow[]`
 *     shape. Raw URLs, tokens, headers, user agents, session
 *     IDs, and customer/vehicle/ticket payload data are
 *     structurally inexpressible at this boundary.
 *   - Extension and provider/LMS evidence are NOT part of the
 *     current `TrainingEvidenceRowKind` union and therefore
 *     cannot influence the derived status.
 *   - The status is presentation-only. Nothing in this module
 *     causes `completion_status`, `completion_confidence`,
 *     `manager_verified_at`, `manual_marked_complete_at`,
 *     `confirmed_at`, or `actual_time_spent_minutes` to change.
 */

import type { TrainingEvidenceDisplayRow } from './evidenceDisplayRows';

// ─── Status union ──────────────────────────────────────────────────

export type TechnicianTrainingEvidenceSummaryStatus =
  | 'no_evidence'
  | 'manual_submitted'
  | 'verification_pending'
  | 'verified'
  | 'revoked';

// ─── Copy constants ────────────────────────────────────────────────
//
// Short labels for the status badge. Long descriptions for the
// neutral paragraph the technician reads. Locked verbatim by F30 §6
// and re-stated by F31 §5; the F32 source-pin asserts each string
// is present in source.

export const TECHNICIAN_TRAINING_EVIDENCE_STATUS_LABELS: Record<
  TechnicianTrainingEvidenceSummaryStatus,
  string
> = {
  no_evidence: 'No evidence',
  manual_submitted: 'Manual completion submitted',
  verification_pending: 'Verification pending',
  verified: 'Confirmed by Manager',
  revoked: 'Verification reversed',
};

export const TECHNICIAN_TRAINING_EVIDENCE_STATUS_DESCRIPTIONS: Record<
  TechnicianTrainingEvidenceSummaryStatus,
  string
> = {
  no_evidence: 'No training activity recorded yet for this item.',
  manual_submitted:
    'You marked this training complete. Manager verification has not been recorded yet.',
  verification_pending: 'Your completion is waiting for manager verification.',
  verified: 'A manager verified this training.',
  revoked:
    'A previous verification was reversed. Ask your manager if you have questions.',
};

// ─── Derivation helper ─────────────────────────────────────────────
//
// Five-step derivation order (top match wins). Each step is
// commented to make the F31 §4 contract explicit at the source
// level.

export function deriveTechnicianTrainingEvidenceSummaryStatus(
  rows: ReadonlyArray<TrainingEvidenceDisplayRow>,
  requiresManagerVerification?: boolean,
): TechnicianTrainingEvidenceSummaryStatus {
  // Step 1. If the latest trust-affecting row by occurredAt is
  // `manager_revocation`, return 'revoked'. "Trust-affecting" is
  // intentionally narrow: only `manager_verification` and
  // `manager_revocation` rows contribute to the latest-row check.
  // Manual completion never overrides a later revocation.
  //
  // Rows with a null / missing / unparseable `occurredAt` sink to
  // the bottom of the ordering (they never beat a real timestamp).
  const trustAffecting = rows.filter(
    (r) =>
      r.kind === 'manager_verification' || r.kind === 'manager_revocation',
  );
  if (trustAffecting.length > 0) {
    let latest: TrainingEvidenceDisplayRow | undefined;
    for (const r of trustAffecting) {
      if (!latest) {
        latest = r;
        continue;
      }
      if (!r.occurredAt) continue;
      if (!latest.occurredAt) {
        latest = r;
        continue;
      }
      // ISO timestamps lexically sort the same way they sort
      // chronologically, which is why drawer code uses
      // `localeCompare` here too.
      if (r.occurredAt.localeCompare(latest.occurredAt) > 0) {
        latest = r;
      }
    }
    if (latest && latest.kind === 'manager_revocation') {
      return 'revoked';
    }
  }

  // Step 2. Any `manager_verification` row exists at all (and the
  // latest trust-affecting row is not a revocation, per step 1).
  if (rows.some((r) => r.kind === 'manager_verification')) {
    return 'verified';
  }

  // Step 3. Manual completion + caller flagged manager verification
  // is required for this artifact / requirement.
  const hasManual = rows.some((r) => r.kind === 'manual_completion');
  if (hasManual && requiresManagerVerification === true) {
    return 'verification_pending';
  }

  // Step 4. Manual completion alone, no manager-verification gate.
  if (hasManual) {
    return 'manual_submitted';
  }

  // Step 5. Fallback — no evidence rows of any kind.
  return 'no_evidence';
}

// ─── Latest-evidence timestamp helper ──────────────────────────────
//
// Convenience for the component: returns the largest non-null
// `occurredAt` across all rows (any kind), or null when nothing is
// available. Pure; mirrors the drawer's "last evidence" semantics.

export function latestEvidenceOccurredAt(
  rows: ReadonlyArray<TrainingEvidenceDisplayRow>,
): string | null {
  let best: string | null = null;
  for (const r of rows) {
    if (!r.occurredAt) continue;
    if (!best || r.occurredAt.localeCompare(best) > 0) {
      best = r.occurredAt;
    }
  }
  return best;
}
